import { WebsocketV1 } from 'src/AppState/websocketV1/WebsocketV1';
import { ApiResponseWrapper } from 'src/utils-mobx/ApiResponseWrapper';
import {
    SdkStaff,
    StaffAccountBasicDataType,
    StaffAccountWalletDataType,
    StaffAccountStaffBasicDataType,
} from 'src/AppState/sdk/staff';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { getSavedCards } from 'src_server/types';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';

const KEEP_ALIVE = 5 * 60 * 1000; //5 minute

export type StaffAccountWalletDataFormattedType = {
    balance: string;
    createdAt: string;
    creditLimit: string;
    currency: CurrencyType;
    fundsLocked: string;
    id: string;
    lastBetAt: string;
    operatorAccumulatedProfit: string;
    operatorBalance: string;
    operatorExternalProfit: string;
    operatorProfit: string;
    playableBalance: string;
    profit: string;
    universe: string;
    updatedAt: string;
    withdrawableBalance: string;
    backofficeWithdrawableBalance: string;
};

class ListCards {
    public cardListAutoMap: AutoMap<string, Resource<getSavedCards.TResponseType>>;

    public constructor(
        private trpcClient: TrpcClient,
        private customer_id: number
    ) {
        this.cardListAutoMap = new AutoMap(
            (provider: string) =>
                new Resource(async (): Promise<getSavedCards.TResponseType> => {
                    return await this.trpcClient.client.paymentsRouter.getSavedCard.query({
                        userId: this.customer_id,
                        provider,
                    });
                })
        );
    }
}

export class UserModel {
    public readonly basicData: ApiResponseWrapper<StaffAccountBasicDataType | null>;
    public readonly basicStaffData: ApiResponseWrapper<StaffAccountStaffBasicDataType | null>;
    public readonly walletData: ApiResponseWrapper<StaffAccountWalletDataType | null>;
    public readonly listCards: ListCards;

    public constructor(sdkStaff: SdkStaff, websocketV1: WebsocketV1, customerId: number, trpcClient: TrpcClient) {
        // use for data which has to be updated frequently
        this.basicData = new ApiResponseWrapper({
            label: null, //`userBasicData ${customerId}`,
            initValue: null,
            keepAlive: KEEP_ALIVE,
            getData: async (): Promise<StaffAccountBasicDataType | null> => sdkStaff.getBasicData(customerId),
            createSubscription: (refresh): (() => void) => {
                const callback = (): void => {
                    refresh();
                    setTimeout(refresh, 5000);
                };

                callback();
                const walletTopic = `${customerId}:Wallet`;

                window.addEventListener('online', callback);
                websocketV1.subscribe(walletTopic, callback);

                return (): void => {
                    window.removeEventListener('online', callback);
                    websocketV1.unsubscribe(walletTopic, callback);
                };
            },
        });

        this.basicStaffData = new ApiResponseWrapper({
            label: null, //`userBasicData ${customerId}`,
            initValue: null,
            keepAlive: KEEP_ALIVE,
            getData: async (): Promise<StaffAccountStaffBasicDataType | null> => sdkStaff.getStaffBasicData(customerId),
            createSubscription: (refresh): (() => void) => {
                const callback = (): void => {
                    refresh();
                    setTimeout(refresh, 5000);
                };

                callback();
                const walletTopic = `${customerId}:Wallet`;

                window.addEventListener('online', callback);
                websocketV1.subscribe(walletTopic, callback);

                return (): void => {
                    window.removeEventListener('online', callback);
                    websocketV1.unsubscribe(walletTopic, callback);
                };
            },
        });

        // use for data which has to be updated frequently
        this.walletData = new ApiResponseWrapper({
            label: null, //`userWalletData ${customerId}`,
            initValue: null,
            keepAlive: KEEP_ALIVE,
            getData: async (): Promise<StaffAccountWalletDataType | null> => sdkStaff.getWalletData(customerId),
            createSubscription: (refresh): (() => void) => {
                const callback = (): void => {
                    refresh();
                    setTimeout(refresh, 5000);
                };

                callback();
                const walletTopic = `${customerId}:Wallet`;

                window.addEventListener('online', callback);
                websocketV1.subscribe(walletTopic, callback);

                return (): void => {
                    window.removeEventListener('online', callback);
                    websocketV1.unsubscribe(walletTopic, callback);
                };
            },
        });

        this.listCards = new ListCards(trpcClient, customerId);
    }
}
